import usePrivateAxios from "@/common/api/usePrivateAxios";
import { useMutation } from "@tanstack/react-query";
import { unFormatCurrency, unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { CreateGovernmentAllocationDistributionQuantityValidationSchema, CreateGovernmentAllocationDistributionValidationSchema } from "../schemas/createGovernmentAllocationDistribution.schema";
import { AxiosError } from "axios";
import { getRailsUpdate } from "@/common/utils/getRailsUpdate";
import { mapToDeletion } from "@/common/utils/railsHelpers";
import { CompanyGovernmentAllocationDistribution } from '../types/governmentAllocationDistribution.type';
import { transformNewProductCategoryDistribution, transformNewProductCategoryDistributionQuantity, transformUpdatedProductCategoryDistribution, transformUpdatedProductCategoryDistributionQuantity } from '../utils/transformNewProductCategoriesDistributions';

type UseUpdateGovernmentAllocationDistributionProps = {
  distribution: CompanyGovernmentAllocationDistribution;
  isConsumptionControlledByQuantity?: boolean;
  onSuccess: () => void;
  onError: (error: AxiosError) => void;
};

const useUpdateGovernmentAllocationDistribution = ({
  distribution,
  isConsumptionControlledByQuantity,
  onSuccess,
  onError,
}: UseUpdateGovernmentAllocationDistributionProps) => {
  const privateAxios = usePrivateAxios();

  return useMutation({
    mutationKey: ['updateGovernmentAllocationDistribution', distribution.id],
    mutationFn: async (form: CreateGovernmentAllocationDistributionValidationSchema | CreateGovernmentAllocationDistributionQuantityValidationSchema) => {
      if (isConsumptionControlledByQuantity) {
        const quantityForm = form as CreateGovernmentAllocationDistributionQuantityValidationSchema;

        const government_allocation_distribution = {
          total_quantity: quantityForm.total_quantity
            ? unFormatUnknownUnit(quantityForm.total_quantity) >= 0
              ? unFormatUnknownUnit(quantityForm.total_quantity)
              : undefined
            : 0,
          product_categories_distributions_attributes: [
            ...getRailsUpdate(
              distribution.product_categories_distributions,
              quantityForm.product_categories_distributions || [],
              (x) => x.product_category.id,
              (x) => x.product_category.id,
              mapToDeletion,
              (newProductCategoryDistribution) => transformNewProductCategoryDistributionQuantity(newProductCategoryDistribution, distribution.product_categories_distributions),
            ),
            ...quantityForm.product_categories_distributions
              ?.filter((productCategoryDistribution) => !!productCategoryDistribution.id)
              ?.map((updatedProductCategoryDistribution) => transformUpdatedProductCategoryDistributionQuantity(updatedProductCategoryDistribution, distribution.product_categories_distributions)) || [],
          ],
        };

        return privateAxios.put(`/billing/government_allocation_distributions/${distribution.id}`, {
          government_allocation_distribution,
        });
      }

      const currencyForm = form as CreateGovernmentAllocationDistributionValidationSchema;

      const government_allocation_distribution = {
        total_value: currencyForm.total_value ? unFormatCurrency(currencyForm.total_value) : 0,
        product_categories_distributions_attributes: [
          ...getRailsUpdate(
            distribution.product_categories_distributions,
            currencyForm.product_categories_distributions || [],
            (x) => x.product_category.id,
            (x) => x.product_category.id,
            mapToDeletion,
            (newProductCategoryDistribution) => transformNewProductCategoryDistribution(newProductCategoryDistribution, distribution.product_categories_distributions),
          ),
          ...currencyForm.product_categories_distributions
            ?.filter((productCategoryDistribution) => !!productCategoryDistribution.id)
            ?.map((updatedProductCategoryDistribution) => transformUpdatedProductCategoryDistribution(updatedProductCategoryDistribution, distribution.product_categories_distributions)) || [],
        ],
      };

      return privateAxios.put(`/billing/government_allocation_distributions/${distribution.id}`, {
        government_allocation_distribution,
      });
    },
    onSuccess,
    onError,
  });
};

export default useUpdateGovernmentAllocationDistribution;
